import React from 'react';
import SlotCard from './SlotCard';
import { formatDateKey } from './calendarUtils';

// ============================================
// DAY COLUMN - Slots for one day (Mon-Sat only)
// ============================================
const DayColumn = ({ date, slots, bookings, clients, workouts, onAddClient, onRemoveClient, onEditSlot, onDeleteSlot, onAddSlot, bulkMode, selectedSlots, onToggleSelect, onClientClick }) => {
  const dateKey = formatDateKey(date);
  const daySlots = slots.filter(s => s.date === dateKey).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const isToday = formatDateKey(new Date()) === dateKey;
  const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

  // Calculate day stats
  const dayBookings = bookings.filter(b => b.date === dateKey && b.status !== 'cancelled');
  const totalCapacity = daySlots.reduce((sum, s) => sum + (s.max_clients || 6), 0);

  // Day names in English
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Selection state for this day
  const daySlotIds = daySlots.map(s => s.id);
  const selectedCount = daySlotIds.filter(id => selectedSlots.has(id)).length;
  const allSelected = daySlotIds.length > 0 && selectedCount === daySlotIds.length;
  const someSelected = selectedCount > 0 && selectedCount < daySlotIds.length;

  // Toggle all slots for this day - pass all IDs at once
  const toggleSelectAll = (e) => {
    e.stopPropagation();
    if (allSelected) {
      // Deselect all day slots - pass array
      onToggleSelect(daySlotIds, false);
    } else {
      // Select all day slots - pass array
      onToggleSelect(daySlotIds, true);
    }
  };

  return (
    <div className={`flex flex-col ${isPast ? 'opacity-50' : ''}`}>
      {/* Day Header with Stats */}
      <div className={`text-center p-3 rounded-t-lg ${isToday ? 'bg-blue-600/30' : 'bg-slate-700/30'}`}>
        <div className="text-xs text-slate-400 uppercase tracking-wide">
          {dayNames[date.getDay()]}
        </div>
        <div className={`text-2xl font-bold ${isToday ? 'text-blue-300' : 'text-white'}`}>
          {date.getDate()}
        </div>
        {totalCapacity > 0 && (
          <div className="text-xs text-slate-400 mt-1">
            {dayBookings.length} / {totalCapacity}
          </div>
        )}

        {/* Select All Checkbox for Bulk Mode */}
        {bulkMode && daySlots.length > 0 && (
          <div
            className="flex items-center justify-center gap-2 mt-2 cursor-pointer hover:bg-purple-500/20 py-1 px-2 rounded transition"
            onClick={toggleSelectAll}
          >
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
              allSelected
                ? 'bg-purple-500 border-purple-500'
                : someSelected
                  ? 'bg-purple-500/50 border-purple-500'
                  : 'bg-transparent border-slate-500'
            }`}>
              {allSelected && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {someSelected && !allSelected && (
                <div className="w-2 h-0.5 bg-white rounded"></div>
              )}
            </div>
            <span className="text-xs text-purple-300">Select all</span>
          </div>
        )}
      </div>

      {/* Slots List */}
      <div className="flex-1 bg-slate-800/20 rounded-b-lg p-2 space-y-2 min-h-[200px]">
        {daySlots.map(slot => (
          <SlotCard
            key={slot.id}
            slot={slot}
            bookings={bookings}
            clients={clients}
            workouts={workouts}
            onAddClient={onAddClient}
            onRemoveClient={onRemoveClient}
            onEdit={onEditSlot}
            onDelete={onDeleteSlot}
            bulkMode={bulkMode}
            isSelected={selectedSlots.has(slot.id)}
            onToggleSelect={() => onToggleSelect(slot.id)}
            onClientClick={onClientClick}
          />
        ))}

        {/* Add Slot Button */}
        {!isPast && !bulkMode && (
          <button
            onClick={() => onAddSlot(date)}
            className="w-full py-2 text-sm text-slate-400 hover:text-blue-300 hover:bg-blue-500/10 rounded border border-dashed border-slate-600 hover:border-blue-400/50 transition"
          >
            + Add slot
          </button>
        )}

        {daySlots.length === 0 && isPast && (
          <div className="text-center text-slate-500 text-sm py-4">
            No slots
          </div>
        )}
      </div>
    </div>
  );
};

export default DayColumn;
